import logger from '../lib/logger';
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Phone, MessageCircle, Check, Plus, Clock,
  AlertTriangle, CheckCircle, Calendar, FileText, Zap,
  TrendingUp, ChevronDown, ChevronRight, MapPin,
  PhoneCall, Building2, ClipboardList, StickyNote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const TASK_TYPES = [
  { value: 'follow_up', label: 'Follow-up', icon: PhoneCall },
  { value: 'site_visit', label: 'Site Visit', icon: MapPin },
  { value: 'call_back', label: 'Call Back', icon: Phone },
  { value: 'document_collection', label: 'Document Collection', icon: ClipboardList },
  { value: 'payment_reminder', label: 'Payment', icon: TrendingUp },
  { value: 'general', label: 'General', icon: FileText },
];

const PRIORITY_CARD = {
  high:   { bg: 'bg-rose-50/60', border: 'border-l-rose-500', badgeBg: 'bg-rose-500 text-white', label: 'High' },
  medium: { bg: 'bg-amber-50/40', border: 'border-l-amber-400', badgeBg: 'bg-amber-400 text-white', label: 'Medium' },
  low:    { bg: 'bg-emerald-50/40', border: 'border-l-emerald-400', badgeBg: 'bg-emerald-500 text-white', label: 'Low' },
};

// ─── Section Header (outside component) ──────
const SectionHeader = ({ title, count, icon: Icon, color, expanded, onToggle, testId }) => (
  <button className="flex items-center w-full gap-3 py-3 group" onClick={onToggle} data-testid={testId}>
    <div className={`p-1.5 rounded-lg ${color}`}><Icon className="w-4 h-4" /></div>
    <span className="text-sm font-semibold text-slate-900">{title}</span>
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${color}`}>{count}</span>
    <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`} />
  </button>
);

// ─── Task Card (outside component) ────────────
const TaskCard = ({ task, variant, onCall, onWhatsApp, onNote, onComplete, rescheduleOpen, setRescheduleOpen, onSnooze }) => {
  const isOverdue = variant === 'overdue';
  const days = task.due_date ? Math.max(0, Math.floor((Date.now() - new Date(task.due_date)) / 86400000)) : 0;
  const priority = PRIORITY_CARD[task.priority] || PRIORITY_CARD.medium;
  const typeConfig = TASK_TYPES.find(t => t.value === task.task_type) || TASK_TYPES[5];
  const TypeIcon = typeConfig.icon;

  const formatDue = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ', ' +
      d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div className={`rounded-xl border border-l-[4px] ${priority.border} ${priority.bg} border-slate-200 overflow-hidden transition-all duration-150`} data-testid={`task-card-${task.id}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${isOverdue ? 'bg-red-100' : 'bg-white/80'} border border-slate-200/50`}>
              <TypeIcon className={`w-4 h-4 ${isOverdue ? 'text-red-600' : 'text-slate-600'}`} />
            </div>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{typeConfig.label}</span>
          </div>
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide ${priority.badgeBg}`}>{priority.label}</span>
        </div>
        <h3 className="font-semibold text-[15px] text-slate-900 leading-snug mb-1.5">{task.title}</h3>
        {(task.lead_name || task.lead_phone) && (
          <div className="flex items-center gap-2 mb-1.5 text-sm">
            {task.lead_name && <span className="font-medium text-slate-700">{task.lead_name}</span>}
            {task.lead_phone && <a href={`tel:${task.lead_phone}`} className="text-blue-600 font-mono text-xs hover:underline">{task.lead_phone}</a>}
          </div>
        )}
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-semibold' : ''}`}>
            <Clock className="w-3 h-3" />
            {isOverdue ? (days === 0 ? 'Overdue today' : `${days}d overdue`) : formatDue(task.due_date)}
          </span>
          {task.assigned_to_name && (
            <><span className="w-0.5 h-0.5 rounded-full bg-slate-300" /><span>{task.assigned_to_name}</span></>
          )}
        </div>
      </div>
      <div className="flex items-stretch border-t border-slate-200/60 bg-white/50">
        {task.lead_phone && (
          <>
            <button className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-green-700 hover:bg-green-50 active:bg-green-100 transition-colors border-r border-slate-200/60" onClick={() => onCall(task)} data-testid={`call-${task.id}`}>
              <Phone className="w-3.5 h-3.5" /> Call
            </button>
            <button className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-green-600 hover:bg-green-50 active:bg-green-100 transition-colors border-r border-slate-200/60" onClick={() => onWhatsApp(task)} data-testid={`wa-${task.id}`}>
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </button>
          </>
        )}
        <button className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-blue-600 hover:bg-blue-50 active:bg-blue-100 transition-colors border-r border-slate-200/60" onClick={() => onNote(task)} data-testid={`note-${task.id}`}>
          <StickyNote className="w-3.5 h-3.5" /> Note
        </button>
        <button className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 active:bg-emerald-100 transition-colors border-r border-slate-200/60" onClick={() => onComplete(task.id)} data-testid={`complete-${task.id}`}>
          <Check className="w-3.5 h-3.5" /> Done
        </button>
        <div className="flex-1 relative">
          <button className="w-full h-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-colors" onClick={(e) => { e.stopPropagation(); setRescheduleOpen(rescheduleOpen === task.id ? null : task.id); }} data-testid={`reschedule-${task.id}`}>
            <Calendar className="w-3.5 h-3.5" /> Later
          </button>
          {rescheduleOpen === task.id && (
            <div className="absolute right-0 bottom-full mb-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20 min-w-[140px]" onClick={(e) => e.stopPropagation()}>
              {[{ days: 1, label: 'Tomorrow' }, { days: 2, label: 'In 2 days' }, { days: 3, label: 'In 3 days' }, { days: 7, label: 'Next week' }].map(opt => (
                <button key={opt.days} className="w-full text-left px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50" onClick={() => onSnooze(task.id, opt.days)}>{opt.label}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Tasks = () => {
  const [todayTasks, setTodayTasks] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeTaskId, setCompleteTaskId] = useState(null);
  const [completeNote, setCompleteNote] = useState('');
  const [leads, setLeads] = useState([]);
  const { user } = useAuth();

  // Collapsible sections
  const [expandOverdue, setExpandOverdue] = useState(true);
  const [expandToday, setExpandToday] = useState(true);
  const [expandUpcoming, setExpandUpcoming] = useState(true);
  const [expandCompleted, setExpandCompleted] = useState(false);
  // Reschedule popover per task
  const [rescheduleOpen, setRescheduleOpen] = useState(null);

  const [createForm, setCreateForm] = useState({
    title: '', description: '', task_type: 'follow_up', priority: 'medium',
    due_date: '', related_to: '', lead_name: '', lead_phone: '',
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [todayRes, overdueRes, statsRes, leadsRes, allRes, completedRes] = await Promise.all([
        api.get('/tasks/today'),
        api.get('/tasks/overdue'),
        api.get('/tasks/stats'),
        api.get('/leads'),
        api.get('/tasks/all?status=pending'),
        api.get('/tasks/all?status=completed'),
      ]);
      setTodayTasks(todayRes.data);
      setOverdueTasks(overdueRes.data);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setCompletedTasks(completedRes.data.slice(0, 30));
      const todayIds = new Set(todayRes.data.map(t => t.id));
      const overdueIds = new Set(overdueRes.data.map(t => t.id));
      setUpcomingTasks(allRes.data.filter(t => !todayIds.has(t.id) && !overdueIds.has(t.id)));
    } catch (e) { logger.error('Tasks fetch error:', e); }
  };

  const handleComplete = (taskId) => { setCompleteTaskId(taskId); setCompleteNote(''); setShowCompleteModal(true); };

  const confirmComplete = async () => {
    if (!completeTaskId) return;
    try {
      const noteParam = completeNote ? `?note=${encodeURIComponent(completeNote)}` : '';
      await api.post(`/tasks/${completeTaskId}/complete${noteParam}`);
      toast.success('Task completed!');
      setShowCompleteModal(false);
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleSnooze = async (taskId, days = 1) => {
    try {
      await api.post(`/tasks/${taskId}/snooze?days=${days}`);
      toast.success(days === 1 ? 'Rescheduled to tomorrow' : `Rescheduled ${days} days`);
      setRescheduleOpen(null);
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleCall = (task) => {
    if (task.lead_phone) {
      window.location.href = `tel:${task.lead_phone}`;
      if (task.related_to) api.post(`/leads/${task.related_to}/log-call`).catch(() => {});
    }
  };

  const handleWhatsApp = (task) => {
    if (!task.lead_phone) return;
    const phone = task.lead_phone.replace(/\D/g, '');
    const name = task.lead_name || '';
    const msg = encodeURIComponent(`Hi ${name}, following up regarding your property inquiry. - Aavaas Consultants`);
    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    const waUrl = isMobile ? `https://wa.me/${phone}?text=${msg}` : `https://web.whatsapp.com/send?phone=${phone}&text=${msg}`;
    const a = document.createElement('a'); a.href = waUrl; a.target = '_blank'; a.rel = 'noopener noreferrer'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
    if (task.related_to) api.post(`/leads/${task.related_to}/log-whatsapp`).catch(() => {});
  };

  const handleAddNote = async (task) => {
    const note = window.prompt(`Add note for ${task.lead_name || task.title}:`);
    if (!note) return;
    if (task.related_to) {
      try {
        await api.post(`/leads/${task.related_to}/notes?content=${encodeURIComponent(note)}`);
        toast.success('Note added');
      } catch { toast.error('Failed'); }
    } else {
      toast.info('Note: ' + note);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const data = { ...createForm };
      if (!data.related_to) delete data.related_to;
      await api.post('/tasks', data);
      toast.success('Task created!');
      setShowCreateModal(false);
      setCreateForm({ title: '', description: '', task_type: 'follow_up', priority: 'medium', due_date: '', related_to: '', lead_name: '', lead_phone: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const getOverdueDays = (dateStr) => {
    if (!dateStr) return 0;
    return Math.max(0, Math.floor((Date.now() - new Date(dateStr)) / 86400000));
  };

  const formatDue = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ', ' +
      d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const getTypeConfig = (type) => TASK_TYPES.find(t => t.value === type) || TASK_TYPES[5];

  // Close reschedule when clicking outside
  useEffect(() => {
    if (rescheduleOpen === null) return;
    const handler = () => setRescheduleOpen(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [rescheduleOpen]);

  return (
    <Layout>
      <div className="aavaas-page" data-testid="tasks-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="aavaas-page-title" data-testid="tasks-title">My Action Plan</h1>
            <p className="aavaas-page-subtitle">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
          <Button className="aavaas-btn-primary" onClick={() => setShowCreateModal(true)} data-testid="create-task-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Task
          </Button>
        </div>

        {/* ─── Top Summary Strip ─────────────────── */}
        {stats && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-4 text-center" data-testid="stat-today">
              <p className="text-2xl lg:text-3xl font-bold text-blue-600 tracking-tight">{stats.due_today}</p>
              <p className="text-[11px] text-slate-500 font-semibold mt-0.5">Today&apos;s Tasks</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-4 text-center" data-testid="stat-overdue">
              <p className={`text-2xl lg:text-3xl font-bold tracking-tight ${stats.missed > 0 ? 'text-red-600' : 'text-slate-300'}`}>{stats.missed}</p>
              <p className="text-[11px] text-slate-500 font-semibold mt-0.5">Overdue</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-4 text-center" data-testid="stat-completed">
              <p className="text-2xl lg:text-3xl font-bold text-emerald-600 tracking-tight">{stats.completed_today}</p>
              <p className="text-[11px] text-slate-500 font-semibold mt-0.5">Completed</p>
            </div>
          </div>
        )}

        {/* ─── OVERDUE Section ─────────────────── */}
        {overdueTasks.length > 0 && (
          <div className="mb-6" data-testid="section-overdue">
            <SectionHeader
              title="Overdue" count={overdueTasks.length}
              icon={AlertTriangle} color="bg-red-50 text-red-600"
              expanded={expandOverdue} onToggle={() => setExpandOverdue(!expandOverdue)}
              testId="toggle-overdue"
            />
            {expandOverdue && (
              <div className="space-y-3 animate-fade-in">
                {overdueTasks.map(t => <TaskCard key={t.id} task={t} variant="overdue" onCall={handleCall} onWhatsApp={handleWhatsApp} onNote={handleAddNote} onComplete={handleComplete} rescheduleOpen={rescheduleOpen} setRescheduleOpen={setRescheduleOpen} onSnooze={handleSnooze} />)}
              </div>
            )}
          </div>
        )}

        {/* ─── DUE TODAY Section ────────────────── */}
        <div className="mb-6" data-testid="section-today">
          <SectionHeader
            title="Due Today" count={todayTasks.length}
            icon={Zap} color="bg-blue-50 text-blue-600"
            expanded={expandToday} onToggle={() => setExpandToday(!expandToday)}
            testId="toggle-today"
          />
          {expandToday && (
            <div className="space-y-3 animate-fade-in">
              {todayTasks.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center">
                  <CheckCircle className="w-10 h-10 mx-auto text-emerald-300 mb-2" />
                  <p className="text-sm font-medium text-slate-500">All caught up for today!</p>
                </div>
              ) : todayTasks.map(t => <TaskCard key={t.id} task={t} variant="today" onCall={handleCall} onWhatsApp={handleWhatsApp} onNote={handleAddNote} onComplete={handleComplete} rescheduleOpen={rescheduleOpen} setRescheduleOpen={setRescheduleOpen} onSnooze={handleSnooze} />)}
            </div>
          )}
        </div>

        {/* ─── UPCOMING Section ────────────────── */}
        {upcomingTasks.length > 0 && (
          <div className="mb-6" data-testid="section-upcoming">
            <SectionHeader
              title="Upcoming" count={upcomingTasks.length}
              icon={Clock} color="bg-amber-50 text-amber-600"
              expanded={expandUpcoming} onToggle={() => setExpandUpcoming(!expandUpcoming)}
              testId="toggle-upcoming"
            />
            {expandUpcoming && (
              <div className="space-y-3 animate-fade-in">
                {upcomingTasks.map(t => <TaskCard key={t.id} task={t} variant="upcoming" onCall={handleCall} onWhatsApp={handleWhatsApp} onNote={handleAddNote} onComplete={handleComplete} rescheduleOpen={rescheduleOpen} setRescheduleOpen={setRescheduleOpen} onSnooze={handleSnooze} />)}
              </div>
            )}
          </div>
        )}

        {/* ─── COMPLETED Section ───────────────── */}
        {completedTasks.length > 0 && (
          <div className="mb-6" data-testid="section-completed">
            <SectionHeader
              title="Completed" count={completedTasks.length}
              icon={CheckCircle} color="bg-emerald-50 text-emerald-600"
              expanded={expandCompleted} onToggle={() => setExpandCompleted(!expandCompleted)}
              testId="toggle-completed"
            />
            {expandCompleted && (
              <div className="space-y-2 animate-fade-in">
                {completedTasks.map(t => (
                  <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100 opacity-70" data-testid={`completed-${t.id}`}>
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-600 line-through truncate">{t.title}</p>
                      <div className="flex gap-3 text-xs text-slate-400 mt-0.5">
                        {t.lead_name && <span>{t.lead_name}</span>}
                        {t.completion_note && <span className="italic truncate">&ldquo;{t.completion_note}&rdquo;</span>}
                        {t.completed_at && <span>{new Date(t.completed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── Agent Performance ────────────────── */}
        {stats?.agent_performance?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-4 mt-4" data-testid="agent-performance">
            <h3 className="font-semibold text-slate-900 mb-3 text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-slate-400" /> Agent Performance
            </h3>
            <div className="space-y-2">
              {stats.agent_performance.map((a) => (
                <div key={a._id || 'unassigned'} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">{a._id || 'Unassigned'}</span>
                  <span className="text-sm font-bold text-emerald-600">{a.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ CREATE TASK MODAL ═══ */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg rounded-xl">
            <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Title *</Label>
                <Input value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} required placeholder="e.g., Follow up with Rajesh for 3BHK" className="mt-1 h-10 rounded-lg" data-testid="task-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type</Label>
                  <Select value={createForm.task_type} onValueChange={(v) => setCreateForm({ ...createForm, task_type: v })}>
                    <SelectTrigger className="mt-1 h-10 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Priority</Label>
                  <Select value={createForm.priority} onValueChange={(v) => setCreateForm({ ...createForm, priority: v })}>
                    <SelectTrigger className="mt-1 h-10 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Due Date *</Label>
                  <Input type="datetime-local" value={createForm.due_date} onChange={(e) => setCreateForm({ ...createForm, due_date: e.target.value })} required className="mt-1 h-10 rounded-lg" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Linked Lead</Label>
                  <Select onValueChange={(v) => {
                    const lead = leads.find(l => l.id === v);
                    setCreateForm({ ...createForm, related_to: v, lead_name: lead?.name || '', lead_phone: lead?.phone || '' });
                  }}>
                    <SelectTrigger className="mt-1 h-10 rounded-lg"><SelectValue placeholder="Select lead" /></SelectTrigger>
                    <SelectContent>{leads.slice(0, 50).map(l => <SelectItem key={l.id} value={l.id}>{l.name} - {l.phone}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</Label>
                <Textarea value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} rows={2} className="mt-1 rounded-lg" />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 h-10 rounded-lg font-semibold" data-testid="submit-task">Create Task</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ═══ COMPLETE WITH NOTE MODAL ═══ */}
        <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
          <DialogContent className="max-w-sm rounded-xl">
            <DialogHeader><DialogTitle>Complete Task</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500 mb-3">Add a note about what happened (optional)</p>
            <Textarea value={completeNote} onChange={(e) => setCompleteNote(e.target.value)} placeholder="e.g., Spoke with client, interested in plot 3028..." rows={3} className="rounded-lg" data-testid="complete-note" />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="flex-1 h-10 rounded-lg" onClick={() => { setCompleteNote(''); confirmComplete(); }}>Skip</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-10 rounded-lg font-semibold" onClick={confirmComplete} data-testid="confirm-complete">
                <Check className="w-4 h-4 mr-1.5" /> Complete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Tasks;
